import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    buildingType: '',
    gutterFootage: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4bql5362rkouuf3qgrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          buildingType: formData.buildingType,
          gutterFootage: formData.gutterFootage,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          buildingType: '',
          gutterFootage: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/faab7be803e325743a7a2cbc00d728b5.jpg)'
          }}
        />
        
        {/* Gradient Overlay - More Subtle and Transparent */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-blue-800/75 to-blue-900/60"></div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight drop-shadow-lg">
                  Commercial Gutter Guards for Large Buildings
                </h1>
                <p className="text-xl lg:text-2xl text-blue-100 drop-shadow-md">
                  Protect metal buildings, warehouses, schools, and industrial facilities with GutterBrush - the simple solution that works where others fail.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-green-400 text-xl"></i>
                  </div>
                  <span className="text-lg drop-shadow">No Tools Required</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-shield-check-line text-green-400 text-xl"></i>
                  </div>
                  <span className="text-lg drop-shadow">10-Year Warranty</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-truck-line text-green-400 text-xl"></i>
                  </div>
                  <span className="text-lg drop-shadow">Free Shipping</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-flag-line text-green-400 text-xl"></i>
                  </div>
                  <span className="text-lg drop-shadow">Made in USA</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="#quote-form" 
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Quote & Samples
                </a>
                <a 
                  href="tel:888-397-9433" 
                  className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Call 888-397-9433
                </a>
              </div>
            </div>

            <div className="lg:text-right">
              <img 
                src="https://www.gutterbrush.com/cdn/shop/files/Commercial_7_8_Inch_GutterBrush.jpg?v=1748953305"
                alt="Commercial GutterBrush Product"
                className="w-full max-w-md mx-auto lg:ml-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Facility Managers in All 50 States
            </h2>
            <p className="text-xl text-gray-600">
              From government facilities to private enterprises, GutterBrush protects America's commercial buildings
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="bg-gray-50 p-8 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-government-line text-blue-600 text-4xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">United States Military Bases</h3>
              <p className="text-gray-600 text-sm">Protecting critical infrastructure at military installations nationwide</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-community-line text-green-600 text-4xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Towns & Municipalities</h3>
              <p className="text-gray-600 text-sm">Trusted by local governments for public buildings and facilities</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-hospital-line text-purple-600 text-4xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Assisted Living Complexes</h3>
              <p className="text-gray-600 text-sm">Ensuring safe, maintenance-free gutter systems for senior care facilities</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg text-center hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-store-3-line text-orange-600 text-4xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Self Storage Franchises</h3>
              <p className="text-gray-600 text-sm">The preferred solution for national storage facility chains</p>
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8 text-center">
            <div className="flex flex-wrap items-center justify-center gap-8 mb-6">
              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-3xl font-bold text-gray-900">50</div>
                  <div className="text-sm text-gray-600">States Served</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                  <i className="ri-building-line text-white text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-3xl font-bold text-gray-900">10,000+</div>
                  <div className="text-sm text-gray-600">Commercial Buildings</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-3xl font-bold text-gray-900">20+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                  <i className="ri-star-line text-white text-xl"></i>
                </div>
                <div className="text-left">
                  <div className="text-3xl font-bold text-gray-900">4.9</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 font-medium">
              Join thousands of facility managers who trust GutterBrush for reliable, long-term commercial gutter protection
            </p>
          </div>
        </div>
      </section>

      {/* Easy Installation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="order-2 md:order-1">
              <img
                src="https://static.readdy.ai/image/8f86aa673083729b29d3e3ce11202afe/cf61b14bf4efa3cbcca61e3b0b8cb1d0.jpeg"
                alt="Man easily installing GutterBrush gutter guard without tools or professional help"
                title="Easy DIY GutterBrush Installation - No Tools Required"
                className="rounded-lg shadow-2xl w-full h-auto object-cover"
              />
            </div>

            {/* Content */}
            <div className="order-1 md:order-2">
              <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                DIY FRIENDLY
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The Easiest Gutter Guard to Install & Maintain
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                No tools. No fasteners. No professional installation required. Save thousands on labor costs and get the job done yourself in minutes.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-coin-line text-green-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Tools or Fasteners Needed</h3>
                    <p className="text-gray-600">Simply place GutterBrush into your gutters. The bristles naturally expand to fill the space - no drilling, screwing, or special equipment required.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-smile-line text-blue-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Professional Installation</h3>
                    <p className="text-gray-600">Save thousands in labor costs. Your maintenance team can install GutterBrush in minutes without hiring contractors or specialized installers.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-refresh-line text-purple-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Minimal Maintenance</h3>
                    <p className="text-gray-600">If overflow occurs, simply remove, shake off debris, and reinstall. No complicated cleaning procedures or special maintenance required.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-orange-600 text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Roof Damage Risk</h3>
                    <p className="text-gray-600">Unlike screens and covers that require attachment under shingles or to fascia, GutterBrush sits inside the gutter with zero roof penetration.</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-orange-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See the Difference GutterBrush Makes
            </h2>
            <p className="text-xl text-gray-600">
              From clogged and overflowing to clean and protected
            </p>
          </div>
          
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://readdy.ai/api/search-image?query=Split%20screen%20before%20and%20after%20comparison%20of%20standing%20seam%20metal%20roof%20commercial%20building%20with%20PBR%20sculpted%20eave%20gutter%20system%2C%20left%20side%20shows%20severely%20clogged%20overflowing%20sculpted%20eave%20gutter%20filled%20with%20leaves%20and%20debris%20causing%20water%20damage%20and%20overflow%20on%20metal%20building%2C%20right%20side%20shows%20same%20clean%20sculpted%20eave%20gutter%20with%20subtle%20black%20cylindrical%20brush%20bristles%20barely%20visible%20creating%20a%20smooth%20rounded%20protective%20profile%20at%20the%20top%20edge%20of%20the%20gutter%20opening%2C%20no%20leaves%20visible%20on%20right%20side%20only%20minimal%20black%20bristles%20showing%2C%20professional%20commercial%20metal%20building%20photography%2C%20industrial%20warehouse%20with%20standing%20seam%20metal%20roof%20and%20pristine%20protected%20sculpted%20eave%20gutter%20system&width=1400&height=600&seq=hero-before-after-metal-building-v3&orientation=landscape"
              alt="Before and After: Clogged PBR Sculpted Eave Gutter vs GutterBrush Protected System on Metal Building"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Commercial Gutter Problems Cost You Money
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Large commercial gutters face unique challenges that standard gutter guards can't solve
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-water-flash-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Water Damage & Flooding</h3>
              <p className="text-gray-600">
                Clogged gutters cause overflow, leading to foundation damage, interior flooding, and costly repairs to your commercial property.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Expensive Maintenance</h3>
              <p className="text-gray-600">
                Regular gutter cleaning for large buildings requires professional crews, equipment, and significant downtime - costing thousands annually.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Standard Guards Don't Fit</h3>
              <p className="text-gray-600">
                Metal building gutters, sculpted eaves, and oversized systems can't accommodate traditional screens or covers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://static.readdy.ai/image/8f86aa673083729b29d3e3ce11202afe/c17b1a6fa1912dd0f09274c7f67291f3.jpeg"
                alt="GutterBrush Installation Process"
                className="w-full rounded-lg shadow-xl"
              />
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                The Simple Solution That Actually Works
              </h2>
              
              <p className="text-xl text-gray-600">
                GutterBrush is the only gutter protection system specifically designed for large commercial gutters. No fasteners, no complex installation - just fill your gutters with 3-foot brush sections.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Fits Any Gutter Shape</h3>
                    <p className="text-gray-600">Works with sculpted eaves, box gutters, K-style, and custom metal building designs</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Prevents All Clogs</h3>
                    <p className="text-gray-600">Debris can't move or clump together, eliminating blockages while allowing water flow</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Easy Installation</h3>
                    <p className="text-gray-600">No tools, no roof work, no fasteners - simply slide sections into clean gutters</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Long-Term Protection</h3>
                    <p className="text-gray-600">Industrial-strength materials with 10-year warranty, built for commercial applications</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why GutterBrush Outperforms Other Gutter Guards
            </h2>
            <p className="text-xl text-gray-600">
              See how GutterBrush compares to traditional screens, covers, and fine mesh systems
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-900 text-white">
                    <th className="px-6 py-4 text-left font-semibold text-sm lg:text-base">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold text-sm lg:text-base bg-orange-500">GutterBrush</th>
                    <th className="px-6 py-4 text-center font-semibold text-sm lg:text-base">Screens & Covers</th>
                    <th className="px-6 py-4 text-center font-semibold text-sm lg:text-base">Fine Mesh Systems</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Compatibility with Large Commercial Gutters
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Designed for oversized gutters (6", 7", 8"+) including box and scupper gutters</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Often not available in large sizes; may not fit industrial profiles</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Typically designed for residential sizes only</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Ease of Installation
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Simple DIY install; no tools or fasteners</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Often requires custom fitting, tools, or professional install</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Requires exact fit, professional install, and sometimes roof modification</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Adaptability to Unique Gutter Shapes
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Flexible and self-adjusting; conforms to irregular and wide profiles</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Rigid structure; struggles with non-standard shapes</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Very limited adaptability; prone to gaps</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Clog Prevention in High Debris Environments
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Keeps debris out while allowing water in; debris dries and blows away</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Tends to collect and trap debris on top</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-yellow-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-alert-line text-yellow-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Fine mesh clogs easily with small debris, especially in commercial settings</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Maintenance Needs
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Minimal; remove and shake only if overflow is observed</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Frequent cleaning of cover/screen surface</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Requires regular cleaning to maintain water flow</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Durability in Harsh Conditions
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">UV-protected and built to withstand heavy rain, ice, and sun</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-yellow-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-alert-line text-yellow-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">May warp or sag under extreme weather</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Fine mesh can dent or deteriorate over time</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Cost-Effectiveness
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Low initial cost, no installation labor, long-term value</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-yellow-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-alert-line text-yellow-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Medium to high cost, plus labor or tools</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">High upfront cost and potential repair expense</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Roof & Building Impact
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">No roof contact or penetration</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Some require attachment under shingles or to fascia</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Often needs modification of roof or drip edge</span>
                      </div>
                    </td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900 text-sm lg:text-base">
                      Availability for Industrial Use
                    </td>
                    <td className="px-6 py-4 text-center bg-green-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Available in sizes for large-scale, flat roof, and industrial buildings</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Limited availability for commercial sizes</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center bg-red-50">
                      <div className="flex flex-col items-center space-y-2">
                        <i className="ri-close-line text-red-600 text-2xl"></i>
                        <span className="text-sm text-gray-700">Rarely found in sizes for non-residential applications</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-lg text-gray-600 mb-6">
              <strong>The Clear Winner:</strong> GutterBrush is the only gutter protection system specifically engineered for commercial and industrial applications.
            </p>
            <a 
              href="#quote-form" 
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your Free Quote Today
            </a>
          </div>
        </div>
      </section>

      {/* Building Types Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perfect for Large Commercial Properties
            </h2>
            <p className="text-xl text-gray-600">
              Trusted by property managers and facility owners nationwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20metal%20warehouse%20building%20with%20large%20industrial%20gutters%2C%20steel%20construction%20commercial%20facility%20with%20prominent%20gutter%20system%2C%20professional%20industrial%20architecture%20with%20metal%20siding%20and%20roofing%2C%20clear%20commercial%20property%20photography&width=400&height=300&seq=warehouse-building&orientation=landscape"
                alt="Metal Warehouse Building"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Metal Buildings & Warehouses</h3>
              <p className="text-gray-600">Pre-engineered steel buildings with sculpted eave gutters</p>
            </div>

            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Large%20self%20storage%20facility%20with%20multiple%20buildings%20and%20extensive%20gutter%20systems%2C%20modern%20storage%20complex%20with%20metal%20roofing%20and%20commercial%20gutters%2C%20professional%20storage%20facility%20architecture%20with%20clean%20lines&width=400&height=300&seq=storage-facility&orientation=landscape"
                alt="Self Storage Facility"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Self Storage Facilities</h3>
              <p className="text-gray-600">Multiple buildings requiring low-maintenance gutter protection</p>
            </div>

            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20school%20building%20with%20large%20commercial%20gutters%2C%20educational%20facility%20with%20metal%20roofing%20and%20extensive%20gutter%20system%2C%20professional%20institutional%20architecture%20with%20prominent%20gutters&width=400&height=300&seq=school-building&orientation=landscape"
                alt="School Building"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Schools & Churches</h3>
              <p className="text-gray-600">Large institutional buildings with oversized gutter systems</p>
            </div>

            <div className="text-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Large%20airplane%20hangar%20with%20massive%20commercial%20gutters%2C%20aviation%20facility%20with%20metal%20construction%20and%20extensive%20gutter%20protection%2C%20industrial%20hangar%20building%20with%20prominent%20gutter%20system&width=400&height=300&seq=airplane-hangar&orientation=landscape"
                alt="Airplane Hangar"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Airplane Hangars</h3>
              <p className="text-gray-600">Massive structures requiring heavy-duty gutter protection</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Get Your Free Quote or Request Samples
            </h2>
            <p className="text-xl text-blue-100">
              Let our experts help you protect your commercial property. We'll provide a custom quote and send sample brushes to test in your gutters.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-8 text-gray-900" data-readdy-form>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Organization
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label htmlFor="buildingType" className="block text-sm font-medium text-gray-700 mb-2">
                  Building Type *
                </label>
                <select
                  id="buildingType"
                  name="buildingType"
                  value={formData.buildingType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select building type</option>
                  <option value="Metal Building/Warehouse">Metal Building/Warehouse</option>
                  <option value="Self Storage Facility">Self Storage Facility</option>
                  <option value="School/Educational">School/Educational</option>
                  <option value="Church/Religious">Church/Religious</option>
                  <option value="Airplane Hangar">Airplane Hangar</option>
                  <option value="Office Building">Office Building</option>
                  <option value="Retail/Shopping Center">Retail/Shopping Center</option>
                  <option value="Manufacturing Facility">Manufacturing Facility</option>
                  <option value="Other Commercial">Other Commercial</option>
                </select>
              </div>

              <div>
                <label htmlFor="gutterFootage" className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Gutter Footage
                </label>
                <select
                  id="gutterFootage"
                  name="gutterFootage"
                  value={formData.gutterFootage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select footage range</option>
                  <option value="Under 100 feet">Under 100 feet</option>
                  <option value="100-300 feet">100-300 feet</option>
                  <option value="300-500 feet">300-500 feet</option>
                  <option value="500-1000 feet">500-1000 feet</option>
                  <option value="1000+ feet">1000+ feet</option>
                  <option value="Not sure">Not sure</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Tell us about your gutter challenges, building details, or any specific requirements..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Get Free Quote & Samples'}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                Thank you! Your request has been submitted. We'll contact you shortly to discuss your needs and help with pricing, samples or placing an order.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                There was an error submitting your request. Please try again or call us at 888-397-9433.
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4 text-center">
              By submitting this form, you agree to be contacted by GutterBrush representatives. We respect your privacy and will never share your information.
            </p>
          </form>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6 flex items-center justify-center lg:justify-start space-x-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-flag-line text-blue-600 text-2xl"></i>
                </div>
                <span className="text-2xl font-bold text-gray-900">Proudly Made in USA</span>
              </div>
              <img 
                src="https://static.readdy.ai/image/8f86aa673083729b29d3e3ce11202afe/6c55001d0e9711705429816f7f41fcfe.webp"
                alt="Satisfied Customer with GutterBrush Installation"
                className="w-full rounded-lg shadow-xl"
              />
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
              </div>
              
              <blockquote className="text-2xl font-medium text-gray-900 italic">
                "The best and only gutter guard solution that worked for the large gutters on my pre-engineered metal building. Easy to install and easy to maintain.  Glad I found this solution"
              </blockquote>
              
              <div className="border-l-4 border-blue-500 pl-6">
                <p className="text-lg text-gray-600 mb-2">
                  <strong>Donnie S.</strong> - Yorktown, VA
                </p>
                <p className="text-sm text-gray-500">Verified Buyer</p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Why Commercial Property Owners Choose GutterBrush:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>Works where other gutter guards fail</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>Reduces maintenance costs by 80%</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>20+ years of proven performance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why GutterBrush Works for Commercial Properties
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-settings-3-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">No Tools Required</h3>
              <p className="text-gray-600 text-center">
                Simply slide 3-foot sections into clean gutters. No fasteners, no roof work, no special equipment needed.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Industrial Strength</h3>
              <p className="text-gray-600 text-center">
                UV-resistant materials built for extreme weather and heavy debris loads. 10-year material warranty.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-refresh-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Easy Maintenance</h3>
              <p className="text-gray-600 text-center">
                When maintenance is needed, simply remove, clean, and reinstall. Renewable protection system.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-drop-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Perfect Water Flow</h3>
              <p className="text-gray-600 text-center">
                Allows maximum water flow while preventing debris from moving and creating clogs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-yellow-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Cost Effective</h3>
              <p className="text-gray-600 text-center">
                Lower cost than professional installation systems. No ongoing maintenance contracts required.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-indigo-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Expert Support</h3>
              <p className="text-gray-600 text-center">
                Our team provides sizing assistance, installation guidance, and ongoing support for your project.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <img 
              src="https://static.readdy.ai/image/8f86aa673083729b29d3e3ce11202afe/b6c6e2b27e2359874cfd4da5db263e63.png"
              alt="GutterBrush Logo"
              className="h-24 mx-auto"
            />
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Protect Your Commercial Property?
          </h2>
          <p className="text-xl mb-8">
            Join thousands of property owners who trust GutterBrush for reliable, long-term gutter protection.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#quote-form" 
              className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your Free Quote
            </a>
            <a 
              href="tel:888-397-9433" 
              className="border-2 border-white text-white hover:bg-white hover:text-orange-500 px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Call 888-397-9433
            </a>
          </div>

          <div className="mt-8 text-center">
            <p className="text-orange-100">
              <i className="ri-truck-line mr-2"></i>
              Free shipping on all orders • 
              <i className="ri-shield-check-line mx-2"></i>
              10-year warranty • 
              <i className="ri-customer-service-2-line mx-2"></i>
              Expert support included
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
